import { Time } from '@gonfalon/datetime/Time';
import { DateFormat } from '@gonfalon/format/constants';
import { TextTruncator } from '@gonfalon/launchpad-experimental/TextTruncator';
import type { schemas } from '@gonfalon/openapi/types';

import { CodeSpan } from '../CodeSpan';

import { EventContextKinds } from './EventContextKinds';

export type EventPreviewColumn = {
  key: string;
  label: string;
  value: (event: schemas['MetricEventDefinitionInstance']) => React.ReactNode;
};

/* takes JSON object and returns a truncated string with the first property of the object & an ellipsis */
const getTruncatedJSON = (jsonData: unknown): string | null => {
  if (!jsonData || typeof jsonData !== 'object') {
    return null;
  }

  const dataStr = JSON.stringify(jsonData);
  if (dataStr.length === 0 || dataStr === '{}') {
    return dataStr || '{}';
  }

  // Handle arrays differently from objects
  if (Array.isArray(jsonData)) {
    return `[${JSON.stringify(jsonData[0])}, ...]`;
  }

  // For objects, find the first property safely
  const firstCommaIndex = dataStr.indexOf(',');
  const firstProperty = firstCommaIndex > -1 ? dataStr.slice(0, firstCommaIndex) : dataStr.slice(0, -1); // Remove closing }

  // Find the first colon after the opening brace
  const colonIndex = firstProperty.indexOf(':', 1); // Start from index 1 to skip opening brace
  if (colonIndex === -1) {
    // No colon found, might be malformed or edge case
    return `${firstProperty}...`;
  }

  const key = firstProperty.slice(0, colonIndex);
  const value = firstProperty.slice(colonIndex + 1);

  // Safely truncate the value - if the value is longer than 5 characters, truncate it to 5 characters and add an ellipsis
  const truncatedValue = value.length > 5 ? `${value.slice(0, 5)}...` : value;
  return `${key}: ${truncatedValue}`;
};

export enum EventKind {
  FEATURE = 'feature',
  CLICK = 'click',
  PAGEVIEW = 'pageview',
  CUSTOM = 'custom',
  IDENTIFY = 'identify',
  INDEX = 'index',
  SUMMARY = 'summary',
  FLATTEN_SUMMARY = 'flattenSummary',
  DEBUG = 'debug',
  UNKNOWN = 'unknown',
}

const EVENT_PREVIEW_COLUMNS: Record<string, EventPreviewColumn> = {
  timestamp: {
    key: 'timestamp',
    label: 'Timestamp',
    value: (event: schemas['MetricEventDefinitionInstance']) => (
      <Time datetime={event.eventTimeMs} dateFormat={DateFormat.YYYY_MM_DD_HH_MM} notooltip includeTimeZone />
    ),
  },
  eventKey: {
    key: 'eventKey',
    label: 'Event key',
    value: (event: schemas['MetricEventDefinitionInstance']) => event.eventKey,
  },
  contextKinds: {
    key: 'contextKinds',
    label: 'Context kind',
    value: (event: schemas['MetricEventDefinitionInstance']) => <EventContextKinds contexts={event.contexts} />,
  },
  source: {
    key: 'source',
    label: 'Source',
    value: (event: schemas['MetricEventDefinitionInstance']) =>
      // eslint-disable-next-line @typescript-eslint/naming-convention
      (event.requestDetail as { event_source?: string })?.event_source,
  },
  data: {
    key: 'eventDetails',
    label: 'Event details',
    value: (event: schemas['MetricEventDefinitionInstance']) => {
      const truncatedJSON = getTruncatedJSON(event);
      return truncatedJSON ? <CodeSpan textValue={truncatedJSON} /> : null;
    },
  },
  value: {
    key: 'value',
    label: 'Value',
    value: (event: schemas['MetricEventDefinitionInstance']) => event.value,
  },
  url: {
    key: 'url',
    label: 'Page URL',
    value: (event: schemas['MetricEventDefinitionInstance']) => <TextTruncator>{event.url}</TextTruncator>,
  },
  selector: {
    key: 'selector',
    label: 'Selector',
    value: (event: schemas['MetricEventDefinitionInstance']) => event.selector,
  },
};

/* Note: don't directly add columns to this object, add them to EVENT_PREVIEW_COLUMNS instead */
const EVENT_PREVIEW_COLUMNS_BY_EVENT_KIND: Record<string, EventPreviewColumn[]> = {
  [EventKind.CUSTOM]: [
    EVENT_PREVIEW_COLUMNS.timestamp,
    EVENT_PREVIEW_COLUMNS.contextKinds,
    EVENT_PREVIEW_COLUMNS.value,
    EVENT_PREVIEW_COLUMNS.data,
  ],
  [EventKind.PAGEVIEW]: [
    EVENT_PREVIEW_COLUMNS.timestamp,
    EVENT_PREVIEW_COLUMNS.contextKinds,
    EVENT_PREVIEW_COLUMNS.value,
    EVENT_PREVIEW_COLUMNS.data,
  ],
  [EventKind.CLICK]: [
    EVENT_PREVIEW_COLUMNS.timestamp,
    EVENT_PREVIEW_COLUMNS.contextKinds,
    EVENT_PREVIEW_COLUMNS.value,
    EVENT_PREVIEW_COLUMNS.data,
  ],
};

export const getEventPreviewTableData = (eventKind: string, _isNumeric?: boolean) => {
  const eventPreviewColumns = EVENT_PREVIEW_COLUMNS_BY_EVENT_KIND[eventKind];
  if (!eventPreviewColumns) {
    return [];
  }
  return eventPreviewColumns;
};
